import React, { useState } from 'react';

const OnboardingWizard = ({ onComplete, playSound, backendUrl }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [permissionStep, setPermissionStep] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [aiConsentChecked, setAiConsentChecked] = useState(false);

  const logConsent = async (userEmail, cameraGranted, locationGranted, gpsPosition) => {
    try {
      const nameParts = userEmail.split('@')[0].replace(/[._]/g, ' ').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const payload = {
        email: userEmail,
        first_name: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        last_name: lastName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        consent_types: [
          ...(cameraGranted ? ['camera'] : []),
          ...(locationGranted ? ['location'] : []),
          'ai_photo_analysis'
        ],
        camera_granted: cameraGranted,
        location_granted: locationGranted,
        ai_consent_granted: true,
        gps_latitude: gpsPosition?.coords?.latitude || null,
        gps_longitude: gpsPosition?.coords?.longitude || null
      };

      const apiBase = backendUrl || localStorage.getItem('backendUrl') || '';
      await fetch(`${apiBase}/api/user/consent-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.error('Consent log failed (non-blocking):', err);
    }
  };

  const validateEmail = (email) => {
    return email.toLowerCase().trim().endsWith('@pyebarkerfs.com');
  };

  const isEmailValid = validateEmail(email);

  const requestCameraPermission = async () => {
    try {
      setPermissionStep('camera');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop stream immediately after permission granted
      stream.getTracks().forEach(track => track.stop());
      localStorage.setItem('cameraPermissionGranted', 'true');
      return true;
    } catch (error) {
      console.error('Camera permission denied:', error);
      return false;
    }
  };

  const requestLocationPermission = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.error('Geolocation not supported');
        resolve({ granted: false, position: null });
        return;
      }

      setPermissionStep('location');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          localStorage.setItem('locationPermissionGranted', 'true');
          resolve({ granted: true, position });
        },
        (error) => {
          console.error('Location permission denied:', error);
          resolve({ granted: false, position: null });
        },
        {
          enableHighAccuracy: true,
          timeout: 60000,
          maximumAge: 0
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setError('Please enter your email');
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setError('Please use your PyeBarkerFS email (@pyebarkerfs.com)');
      return;
    }
    
    // Clear any previous errors
    setError('');
    setPermissionError('');
    setIsRequestingPermissions(true);
    
    localStorage.setItem('pwaOnboardingComplete', 'true');
    localStorage.setItem('submittedOnBehalfOf', trimmedEmail);
    localStorage.setItem('ai_consent_accepted', 'true');

    // Sequential permission flow (best-effort)
    const cameraGranted = await requestCameraPermission();
    if (!cameraGranted) {
      setPermissionStep('error');
      setPermissionError('camera');
      setIsRequestingPermissions(false);
      return;
    }

    const locationResult = await requestLocationPermission();
    if (!locationResult.granted) {
      setPermissionStep('error');
      setPermissionError('location');
      setIsRequestingPermissions(false);
      return;
    }

    // Both permissions granted — log consent
    setPermissionsGranted(true);
    await logConsent(trimmedEmail, true, true, locationResult.position);
    
    if (playSound) {
      playSound('level-up', 'onboarding-complete');
    }
    
    setIsRequestingPermissions(false);
    onComplete(trimmedEmail);
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[9999] flex items-center justify-center p-3 sm:p-4 animate-fadeIn">
      <div className="bg-[var(--pbfs-navy)] backdrop-blur-md rounded-2xl p-5 sm:p-8 border border-white/10 max-w-md w-full shadow-2xl animate-scaleIn max-h-[95dvh] overflow-y-auto">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img 
            src="/images/PBFS-white.png" 
            alt="PBFS TagQuest" 
            className="w-24 sm:w-32 h-auto"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        {/* Title */}
        <h2 className="text-2xl sm:text-3xl font-bold text-pb-green mb-4 sm:mb-6 text-center">
          Welcome to TagQuest
        </h2>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Enter Your PBFS Email
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="first.last@pyebarkerfs.com"
                className="w-full px-4 py-3 pr-12 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 text-white placeholder-white/50 placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)] transition-all"
                autoFocus
                disabled={isRequestingPermissions}
              />
              {/* Green Checkmark on Valid Email */}
              {isEmailValid && email && !isRequestingPermissions && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-2xl text-pb-green">✓</span>
                </div>
              )}
            </div>
            {error && (
              <p className="text-red-400 text-sm mt-2">{error}</p>
            )}
          </div>
          
          {/* Required Permissions & AI Consent */}
          {isEmailValid && (
            <div className="space-y-3 py-3 sm:py-4 border-t border-white/20">
              <details className="group" open>
                <summary className="text-white/80 text-sm cursor-pointer flex items-center hover:text-white transition-colors">
                  <span className="mr-2 transition-transform group-open:rotate-90">▶</span>
                  Required permissions
                </summary>
                
                <div className="mt-4 space-y-3 pl-2">
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--pbfs-green)] mt-2 flex-shrink-0"></div>
                    <div>
                      <div className="text-white text-sm">Camera Access</div>
                      <div className="text-white/50 text-xs mt-0.5">Scan fire extinguisher tags</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--pbfs-green)] mt-2 flex-shrink-0"></div>
                    <div>
                      <div className="text-white text-sm">Location Access</div>
                      <div className="text-white/50 text-xs mt-0.5">Identify nearby businesses</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 mt-2">
                    <label className="flex items-start space-x-2.5 sm:space-x-3 cursor-pointer w-full">
                      <input
                        type="checkbox"
                        checked={aiConsentChecked}
                        onChange={(e) => setAiConsentChecked(e.target.checked)}
                        className="mt-0.5 w-5 h-5 min-w-[20px] rounded border-white/30 bg-white/10 text-[var(--pbfs-green)] focus:ring-[var(--pbfs-green)] flex-shrink-0 cursor-pointer accent-[#0DB14B]"
                      />
                      <div className="min-w-0">
                        <div className="text-white text-sm">AI Photo Analysis</div>
                        <div className="text-white/50 text-xs mt-0.5 leading-snug">Photos analyzed by Google Gemini AI for inspection data</div>
                        <details className="mt-1">
                          <summary className="text-white/40 text-xs cursor-pointer hover:text-white/60 transition-colors">Learn more</summary>
                          <p className="text-white/40 text-xs mt-1 leading-relaxed">
                            When you submit a photo, it is sent to Google Gemini AI (enterprise API) to read the inspection date and check compliance. Photos are processed securely and are not stored by Google after analysis.{' '}
                            <button
                              type="button"
                              className="text-[var(--pbfs-blue)] underline inline"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                try {
                                  const { Capacitor } = await import('@capacitor/core');
                                  if (Capacitor.isNativePlatform()) {
                                    const { Browser } = await import('@capacitor/browser');
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://tagquest.pyebarkerfs.com';
                                    await Browser.open({ url: `${backendUrl}/privacy.html`, presentationStyle: 'popover' });
                                    return;
                                  }
                                } catch (_) { /* web build fallback */ }
                                window.open('/privacy.html', '_blank', 'noopener,noreferrer');
                              }}
                            >Privacy Policy</button>
                          </p>
                        </details>
                      </div>
                    </label>
                  </div>
                </div>
              </details>
            </div>
          )}
          
          {/* Permission Request Status - Elegant, minimal */}
          {isRequestingPermissions && (
            <div className="mt-4 p-4 bg-white/5 border border-white/20 rounded-xl backdrop-blur-sm">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-[var(--pbfs-green)]"></div>
                </div>
                <span className="text-white/90 text-sm">
                  {permissionStep === 'camera' && 'Requesting camera access...'}
                  {permissionStep === 'location' && 'Requesting location access...'}
                </span>
              </div>
            </div>
          )}
          
          {/* Permission Error - Clean and professional */}
          {permissionStep === 'error' && (
            <div className="mt-4 p-5 bg-white/5 border border-red-400/40 rounded-xl backdrop-blur-sm space-y-4 animate-scaleIn">
              <div className="text-red-400 text-sm">
                {permissionError === 'camera' ? 'Camera' : 'Location'} Access Required
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                {permissionError === 'camera' 
                  ? 'Camera access is needed to scan fire extinguisher tags.'
                  : 'Location access is needed to identify nearby businesses.'}
              </p>
              
              {/* Retry Button - Primary Action */}
              <button
                onClick={async () => {
                  setPermissionStep('');
                  setPermissionError('');
                  setIsRequestingPermissions(true);
                  
                  // Retry camera permission
                  const cameraGranted = await requestCameraPermission();
                  if (!cameraGranted) {
                    setPermissionStep('error');
                    setPermissionError('camera');
                    setIsRequestingPermissions(false);
                    return;
                  }
                  
                  // Try location permission
                  const locationResult = await requestLocationPermission();
                  if (!locationResult.granted) {
                    setPermissionStep('error');
                    setPermissionError('location');
                    setIsRequestingPermissions(false);
                    return;
                  }
                  
                  // Both granted — log consent and complete
                  setPermissionsGranted(true);
                  localStorage.setItem('pwaOnboardingComplete', 'true');
                  localStorage.setItem('submittedOnBehalfOf', email.trim());
                  await logConsent(email.trim(), true, true, locationResult.position);
                  
                  if (playSound) {
                    playSound('level-up', 'onboarding-complete');
                  }
                  
                  setIsRequestingPermissions(false);
                  onComplete(email.trim());
                }}
                className="w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white py-3.5 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg tracking-wide"
              >
                Try Again
              </button>

              <button
                type="button"
                onClick={async () => {
                  localStorage.setItem('pwaOnboardingComplete', 'true');
                  localStorage.setItem('submittedOnBehalfOf', email.trim());
                  await logConsent(email.trim(), false, false, null);
                  onComplete(email.trim());
                }}
                className="w-full bg-white/10 hover:bg-white/15 text-white py-3.5 px-6 rounded-xl transition-all duration-300 border border-white/10"
              >
                Continue without permissions
              </button>
              
              <details className="text-white/60 text-xs">
                <summary className="cursor-pointer hover:text-white/80 mb-2">
                  How to enable {permissionError} access
                </summary>
                <ol className="list-decimal list-inside space-y-1.5 ml-2 mt-3 text-white/50">
                  <li>Click the lock icon in your browser's address bar</li>
                  <li>Find "{permissionError === 'camera' ? 'Camera' : 'Location'}" permissions</li>
                  <li>Change setting to "Allow"</li>
                  <li>Click "Try Again" above</li>
                </ol>
              </details>
            </div>
          )}
          
          {/* Continue Button - Hidden until email is valid and AI consent checked */}
          {isEmailValid && (
            <button
              type="submit"
              disabled={isRequestingPermissions || !aiConsentChecked}
              className={`w-full font-bold py-3 px-6 rounded-xl transition-all duration-500 shadow-xl transform animate-fadeIn ${
                !isRequestingPermissions && aiConsentChecked
                  ? 'bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
                  : 'bg-[var(--pbfs-green)]/30 text-white/50 cursor-not-allowed'
              }`}
            >
              {isRequestingPermissions ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white text-[var(--pbfs-white)]"></div>
                  <span>Requesting permissions...</span>
                </span>
              ) : (
                <span className="tracking-wide">CONTINUE →</span>
              )}
            </button>
          )}
          
          {/* Success indicator - Clean */}
          {permissionsGranted && (
            <div className="flex items-center justify-center space-x-2 animate-fadeIn">
              <div className="w-4 h-4 rounded-full bg-[var(--pbfs-green)] flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-white/60 text-xs">
                Setup complete
              </span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default OnboardingWizard;
